/**
 * The tenant schema the CRM renders from: the compiled registries overlaid with the tenant's installed apps.
 *
 * Until GET /schema answers (or if it fails) the compiled registries alone are used, so built-in pages render as before.
 * Components look types up here instead of in NODE_REGISTRY / EDGE_REGISTRY, and App.tsx generates list, detail and
 * board routes from `nav`.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuthStore } from '@diggens/user-management';
import { useTenantSchema } from '@diggens/shared-hooks';
import { buildTenantRegistry, findListPath, type SchemaEdgeType, type SchemaNavCategory, type SchemaNodeType, type SchemaView, type TenantSchema } from '@diggens/types/apps';
import { MODULE_REGISTRY } from '@diggens/types/graph/modules';

export interface SchemaContextValue {
  schema: TenantSchema;
  /** True once the schema came from the API rather than the compiled fallback. */
  isLive: boolean;
  isLoading: boolean;
  getNodeDefinition: (nodeType: string) => SchemaNodeType | undefined;
  getEdgeDefinition: (edgeType: string) => SchemaEdgeType | undefined;
  /** The list page for a node type: an installed app's nav, else the built-in module route. */
  getListPath: (nodeType: string) => string | undefined;
  nav: SchemaNavCategory[];
  views: SchemaView[];
}

const COMPILED_SCHEMA: TenantSchema = buildTenantRegistry([]).schema;

/** The built-in list route of a node type: its module's plain route for that type, never a pipeline or filtered one. */
function builtinListPath(nodeType: string): string | undefined {
  for (const module of Object.values(MODULE_REGISTRY)) {
    for (const route of module.routes) {
      if (route.nodeType === nodeType && !route.filter && !route.path.endsWith('/pipeline')) return route.path;
    }
  }
  return undefined;
}

function buildValue(schema: TenantSchema, isLive: boolean, isLoading: boolean): SchemaContextValue {
  return {
    schema,
    isLive,
    isLoading,
    getNodeDefinition: (nodeType) => schema.nodeTypes[nodeType],
    getEdgeDefinition: (edgeType) => schema.edgeTypes[edgeType],
    getListPath: (nodeType) => findListPath(schema, nodeType) ?? builtinListPath(nodeType),
    nav: schema.nav,
    views: schema.views,
  };
}

const SchemaContext = createContext<SchemaContextValue>(buildValue(COMPILED_SCHEMA, false, false));

export function SchemaProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((state) => state.user);
  const { data, isLoading, isError } = useTenantSchema({ enabled: !!user });

  const value = useMemo(() => {
    if (data && !isError) return buildValue(data, true, false);
    return buildValue(COMPILED_SCHEMA, false, !!user && isLoading);
  }, [data, isError, isLoading, user]);

  return <SchemaContext.Provider value={value}>{children}</SchemaContext.Provider>;
}

export function useSchema(): SchemaContextValue {
  return useContext(SchemaContext);
}
